import axios from "axios";
import { batch } from "react-redux";
import {
  FETCH_BOOKS,
  UPDATE_BOOK,
  UPDATE_BOOKSET,
  LOADING_BOOKS,
  FETCH_UNITS,
  FETCH_SECTIONS
} from "./type";

import mediaServerAddress from "../config/mediaServerAddress";

/**
 * BOOK
 */

// fetch all the books
export const fetchBooks = (options, callback) => async (dispatch, getStore) => {
  // Skip fetch if books for this booksetId already loaded and skipIfLoaded is true (default)
  const skipIfLoaded = options?.skipIfLoaded !== false;
  if (skipIfLoaded && options?.booksetId) {
    const { book } = getStore();
    const existingBooks = Object.values(book?.bookMap || {});
    const booksForBookset = existingBooks.filter(
      (b) => b._bookset === options.booksetId || b.booksetId === options.booksetId
    );
    if (booksForBookset.length > 0) {
      // Books for this bookset already loaded, skip API call
      if (callback) callback();
      return;
    }
  }

  dispatch({ type: LOADING_BOOKS, payload: {} });

  let query = ``;

  if (options.booksetId !== undefined)
    query = `${query}&booksetId=${options.booksetId}`;
  if (options.bookTypeId !== undefined)
    query = `${query}&bookTypeId=${options.bookTypeId}`;
  if (options.isHidden !== undefined)
    query = `${query}&isHidden=${options.isHidden}`;

  const route = `book${query ? `?${query.slice(1)}` : ``}`;

  try {
    const {
      data: { bookMap }
    } = await axios.get(route);

    dispatch({ type: FETCH_BOOKS, payload: { bookMap } });

    if (callback) callback();
  } catch (error) {
    // Reset loading state on error - error UI handled by axios interceptor
    dispatch({ type: FETCH_BOOKS, payload: { bookMap: {} } });
  }
};

// save edit book
export const saveEditBook =
  (
    formProps,
    title,
    bookId,
    coverImage,
    src,
    isHidden,
    level,
    callback
  ) =>
  async (dispatch, getStore) => {
    const uselessImages = {};
    let _image = coverImage;

    if (coverImage) uselessImages[coverImage._id] = true;

    if (src) {
      if (formProps[`image_0`]) {
        const fd = new FormData();

        fd.append(`image`, formProps[`image_0`]);

        // upload new image
        const {
          data: { image }
        } = await mediaServerAddress.post(`image`, fd);

        _image = image;
      } else {
        // User didn't upload new image, but src exists, so keep the existing image
        if (coverImage) delete uselessImages[coverImage._id];
      }
    } else {
      // src is empty - user either deleted the image or there was no image
      _image = null;
    }

    const patchData = {
      title: title !== undefined ? title : "",
      imageId: _image ? _image._id : null,
      isHidden: isHidden !== undefined ? isHidden : false,
      level: level !== undefined ? level : 0
    };

    try {
      const {
        data: { book }
      } = await axios.patch(`book/${bookId}`, {
        patch: {
          action: `changeCover`,
          data: patchData
        }
      });

      // clean up old images
      await axios.delete(`image`, {
        data: { imageIds: Object.keys(uselessImages) }
      });

      dispatch({ type: UPDATE_BOOK, payload: { book } });

      if (callback) callback();
    } catch (error) {
      throw error;
    }
  };

//save add book
export const saveAddBook =
  (
    { title, booksetId, booktypeId, isHidden, level, ...formProps },
    callback
  ) =>
  async (dispatch, getStore) => {
    let imageId = null;

    if (formProps[`image_0`]) {
      const fd = new FormData();

      fd.append(`image`, formProps[`image_0`]);

      // upload new image
      const {
        data: { image }
      } = await mediaServerAddress.post(`image`, fd);

      imageId = image._id;
    }

    const {
      data: { book }
    } = await axios.post(`book`, {
      title: title,
      booksetId,
      booktypeId,
      isHidden,
      imageId,
      level: parseInt(level, 10)
    });
    dispatch({ type: UPDATE_BOOK, payload: { book } });

    //update bookset.bookTypes._book
    const {
      data: { bookset }
    } = await axios.patch(`bookset/${booksetId}`, {
      patch: {
        action: "addBook",
        data: {
          booktypeId,
          bookId: book._id
        }
      }
    });
    dispatch({ type: UPDATE_BOOKSET, payload: { bookset } });

    if (callback) callback();
  };

// move book to trash (soft delete)
export const deleteBook =
  (_booksetId, _booktypeId, _bookId, callback) =>
  async (dispatch) => {
    try {
      const {
        data: { bookset }
      } = await axios.patch(`bookset/${_booksetId}`, {
        patch: {
          action: "trashBook",
          data: { booktypeId: _booktypeId, bookId: _bookId }
        }
      });

      dispatch({ type: UPDATE_BOOKSET, payload: { bookset } });
    } finally {
      if (callback) callback();
    }
  };

// restore book from trash
export const restoreBook =
  (booksetId, booktypeId, bookId, callback) =>
  async (dispatch, getStore) => {
    try {
      const {
        data: { bookset }
      } = await axios.patch(`bookset/${booksetId}`, {
        patch: {
          action: "restoreBook",
          data: { booktypeId, bookId }
        }
      });

      // Clear stale deletedAt in bookMap so the book is clean after restore
      const { book: bookState } = getStore();
      const staleBook = bookState.bookMap[bookId];
      if (staleBook) {
        dispatch({
          type: UPDATE_BOOK,
          payload: { book: { ...staleBook, deletedAt: null } }
        });
      }

      dispatch({ type: UPDATE_BOOKSET, payload: { bookset } });
    } finally {
      if (callback) callback();
    }
  };

// permanently delete book from trash (super only)
export const permanentDeleteBook =
  (booksetId, booktypeId, bookId, callback) =>
  async (dispatch) => {
    try {
      const {
        data: { bookset }
      } = await axios.patch(`bookset/${booksetId}`, {
        patch: {
          action: "permanentDeleteBook",
          data: { booktypeId, bookId }
        }
      });

      dispatch({ type: UPDATE_BOOKSET, payload: { bookset } });
    } finally {
      if (callback) callback();
    }
  };

// fetch trashed books for a bookType into bookMap
export const fetchTrashedBooks =
  (booksetId, booktypeId, callback) =>
  async (dispatch) => {
    try {
      const {
        data: { bookMap }
      } = await axios.get(
        `book/trash?booksetId=${booksetId}&booktypeId=${booktypeId}`
      );

      dispatch({ type: FETCH_BOOKS, payload: { bookMap } });
    } catch (_err) {
      // Silently fail — panel will show empty state
      dispatch({ type: FETCH_BOOKS, payload: { bookMap: {} } });
    } finally {
      if (callback) callback();
    }
  };

// move book
export const moveBook =
  ({ booksetId, booktypeId, bookId }, from, to, callback) =>
  async (dispatch, getStore) => {
    const {
      data: { bookset }
    } = await axios.patch(`bookset/${booksetId}`, {
      patch: {
        action: "moveBook",
        data: { booktypeId, bookId, from, to }
      }
    });

    dispatch({ type: UPDATE_BOOKSET, payload: { bookset } });

    if (callback) callback();
  };

// duplicate unit
export const duplicateUnits =
  ({ unitIds, bookId }, callback, mode) =>
  async (dispatch, getStore) => {
    const {
      data: { book }
    } = await axios.patch(`book/${bookId}`, {
      patch: {
        action: "duplicate_units",
        data: { unitIds, mode: mode || "" }
      }
    });

    const {
      data: { unitMap }
    } = await axios.get(`unit?bookId=${bookId}`);

    const {
      data: { sectionMap }
    } = await axios.get(`section?bookId=${bookId}`);

    batch(() => {
      dispatch({ type: UPDATE_BOOK, payload: { book } });
      dispatch({ type: FETCH_UNITS, payload: { unitMap } });
      dispatch({ type: FETCH_SECTIONS, payload: { sectionMap } });
    });

    if (callback) callback();
  };
